import { z } from "zod";
import { CreatePhysicalClientDto, PhysicalClient, UpdatePhysicalClientDto } from "@/lib/api";
import { formatPhone } from "@/lib/formatters";
import { cpfSchema, normalizeCpf } from "@/lib/validators/cpf";

/** Mesmas regras de Validators/PhysicalClientValidators.cs; as mensagens da API (400) também são exibidas no campo. */

export const CONTACT_REQUIRED_MESSAGE = "Informe um e-mail ou celular.";
const PHONE_MESSAGE = "Informe o telefone com DDD (10 ou 11 dígitos).";

const digits = (value: string) => value.replace(/\D/g, "");
const collapse = (value: string) => value.trim().replace(/\s+/g, " ");

const phone = z
  .string()
  .refine((value) => !value.trim() || (/^[\d\s()-]+$/.test(value) && [10, 11].includes(digits(value).length)), PHONE_MESSAGE);

const fields = {
  fullName: z
    .string()
    .transform(collapse)
    .pipe(
      z
        .string()
        .min(1, "Informe o nome completo.")
        .min(3, "O nome deve ter pelo menos 3 caracteres.")
        .max(200, "O nome deve ter no máximo 200 caracteres."),
    ),
  email: z
    .string()
    .trim()
    .max(254, "O e-mail deve ter no máximo 254 caracteres.")
    .refine((value) => !value || z.string().email().safeParse(value).success, "Informe um e-mail válido."),
  mobilePhone: phone,
  phone,
  customerGroupId: z.number().nullable(),
  erpCode: z.string().trim().max(50, "O código no ERP deve ter no máximo 50 caracteres."),
};

const requireContact = <T extends { email: string; mobilePhone: string }>(values: T, ctx: z.RefinementCtx) => {
  if (!values.email.trim() && !values.mobilePhone.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["email"], message: CONTACT_REQUIRED_MESSAGE });
  }
};

export const createPhysicalClientSchema = z.object({ cpf: cpfSchema, ...fields }).superRefine(requireContact);

export const updatePhysicalClientSchema = z
  .object({ ...fields, comment: z.string().trim().max(500, "A observação deve ter no máximo 500 caracteres.") })
  .superRefine(requireContact);

export type CreatePhysicalClientFormValues = z.input<typeof createPhysicalClientSchema>;
export type UpdatePhysicalClientFormValues = z.input<typeof updatePhysicalClientSchema>;

export const EMPTY_CREATE_FORM: CreatePhysicalClientFormValues = {
  cpf: "",
  fullName: "",
  email: "",
  mobilePhone: "",
  phone: "",
  customerGroupId: null,
  erpCode: "",
};

const orNull = (value: string) => value.trim() || null;
const digitsOrNull = (value: string) => digits(value) || null;

/** Corpo do cadastro: CPF e telefones só com dígitos, vazios como null. */
export const toCreatePayload = (values: CreatePhysicalClientFormValues): CreatePhysicalClientDto => ({
  cpf: normalizeCpf(values.cpf),
  fullName: collapse(values.fullName),
  email: orNull(values.email),
  mobilePhone: digitsOrNull(values.mobilePhone),
  phone: digitsOrNull(values.phone),
  customerGroupId: values.customerGroupId,
  erpCode: orNull(values.erpCode),
});

/** Corpo da edição: substitui todos os campos editáveis; null limpa (customerGroupId null remove o grupo). */
export const toUpdatePayload = (values: UpdatePhysicalClientFormValues): UpdatePhysicalClientDto => ({
  fullName: collapse(values.fullName),
  email: orNull(values.email),
  mobilePhone: digitsOrNull(values.mobilePhone),
  phone: digitsOrNull(values.phone),
  customerGroupId: values.customerGroupId,
  erpCode: orNull(values.erpCode),
  comment: orNull(values.comment),
});

/** Valores do formulário de edição a partir do cadastro (sem CPF). */
export const toUpdateFormValues = (client: PhysicalClient): UpdatePhysicalClientFormValues => ({
  fullName: client.fullName,
  email: client.email ?? "",
  mobilePhone: formatPhone(client.mobilePhone),
  phone: formatPhone(client.phone),
  customerGroupId: client.customerGroupId ?? null,
  erpCode: client.erpCode ?? "",
  comment: "",
});
